import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  FormContainer,
  LaurenQuoteMobile,
  NextButton,
  Prompt,
  QuestionContainer,
  QuestionPageContainer,
  RadioInput,
  StringInput,
} from "~/OnboardingApp/components";
import { useOnboarding } from "~/OnboardingApp/hooks/useOnboarding";
import { useSubmitOnboardingQuestion } from "~/OnboardingApp/hooks/useSubmitOnboardingQuestion";
import { messages } from "~/utils/validations";

type Values = {
  journeyLooksLike: string;
  journeyOtherDetails: string;
};

// "other" is the internal TreatmentPlanAnswers enum value persisted on the backend.
const OTHER = "other";

const validationSchema = Yup.object({
  journeyLooksLike: Yup.string().required(messages.required),
  journeyOtherDetails: Yup.string().test(
    "journeyLooksLike",
    'This field is required when "Other" is selected.',
    function (value) {
      const { journeyLooksLike } = this.parent;
      return journeyLooksLike === OTHER ? Boolean(value?.trim()) : true;
    },
  ),
});

// Values are the internal TreatmentPlanAnswers enum values stored on the backend;
// labels are what the user sees. Keeping them paired means the saved answer
// re-selects correctly on revisit without a label<->value mapping in the updater.
const options = [
  {
    label: "Just getting started/doing testing",
    value: "fertility_check",
  },
  { label: "Timed intercourse at home", value: "tic_oi" },
  { label: "IUI", value: "iui" },
  { label: "IVF", value: "ivf" },
  { label: "Pursuing surrogacy", value: "surrogacy" },
  { label: "Pursuing egg donor", value: "donor_egg" },
  { label: "Pursuing fertility preservation", value: "fertility_preservation" },
  { label: "I am a surrogate/donor", value: "surrogate_donor" },
  { label: "Other", value: OTHER },
];

export const JourneyLooksLikePage: FC = () => {
  const { t } = useTranslation();
  const { journeyLooksLike, journeyOtherDetails } = useOnboarding();

  // This step is necessary because this used to be multi-select and is stored as an Array on the backend.
  // We convert the string value submitted on this page to an Array before persisting in the Onboarding::Updater
  const selectedJourney = journeyLooksLike?.[0] || "";

  const { onSubmit } = useSubmitOnboardingQuestion();

  const initialValues: Values = {
    journeyLooksLike: selectedJourney || "",
    journeyOtherDetails: journeyOtherDetails || "",
  };

  const formik = useFormik({
    initialValues,
    onSubmit: (values, formikHelpers) => {
      // Convert the single string value to an array as expected by the backend
      const transformedValues = {
        ...values,
        journeyLooksLike: values.journeyLooksLike ? [values.journeyLooksLike] : [],
      };
      return onSubmit(transformedValues, formikHelpers);
    },
    validationSchema,
    validateOnMount: true,
  });

  const isAutoAdvance = (option: string) => option !== OTHER;
  const { values: formikValues, setFieldValue } = formik;

  useEffect(() => {
    if (formikValues?.journeyLooksLike !== OTHER && formikValues?.journeyOtherDetails !== "") {
      setFieldValue("journeyOtherDetails", "");
    }
  }, [formikValues.journeyLooksLike, formikValues.journeyOtherDetails, setFieldValue]);

  const showAdditionalQuestion = formikValues?.journeyLooksLike === OTHER;

  return (
    <QuestionPageContainer>
      <FormContainer formik={formik}>
        <QuestionContainer>
          <Prompt
            comment={t("onboarding.journeyLooksLikePage.comment")}
            description={t("onboarding.journeyLooksLikePage.description")}
            question={t("onboarding.journeyLooksLikePage.question")}
          />
          <LaurenQuoteMobile>{t("onboarding.journeyLooksLikePage.comment")}</LaurenQuoteMobile>
          <RadioInput
            autoAdvance={isAutoAdvance}
            id="journeyLooksLike"
            options={options}
            defaultValue={selectedJourney}
          />
        </QuestionContainer>
        {showAdditionalQuestion && (
          <QuestionContainer my={5}>
            <Prompt question={t("onboarding.journeyLooksLikePage.additionalQuestion.question")} />
            <StringInput
              id="journeyOtherDetails"
              placeholder={t("onboarding.journeyLooksLikePage.additionalQuestion.placeholder")}
            />
          </QuestionContainer>
        )}
        <NextButton />
      </FormContainer>
    </QuestionPageContainer>
  );
};
